import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useState, type DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { meQuery } from "../../../api/queries";
import { ConfirmDialog } from "../../ops/shared/ConfirmDialog";
import { useOrg } from "../../org/OrgProvider";
import { FormDialog } from "../../organisation/RoleDialog";
import { isAdminRole } from "../../organisation/roles";
import {
  deleteImage,
  fileProblem,
  IMAGE_LIMIT,
  imageBlobQuery,
  imageError,
  imagesKey,
  imagesQuery,
  noteConfig,
  pictureUrl,
  uploadImage,
  type OrgImage,
} from "../images";

const field = "mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm";

/** Add or change a note (`text` widget): plain text, line breaks kept, at most 2000 characters. */
export function NoteDialog({
  open,
  initial,
  onDone,
  onClose,
}: {
  open: boolean;
  initial?: string;
  onDone: (config: { text: string }) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  // The caller mounts the dialog while it is open, so it starts from the note it changes.
  const [text, setText] = useState(initial ?? "");
  const config = noteConfig(text);
  return (
    <FormDialog
      open={open}
      title={t(initial ? "images.note.changeTitle" : "images.note.title")}
      submitLabel={t(initial ? "images.note.save" : "images.note.add")}
      ready={!!config}
      onSubmit={() => {
        if (config) onDone(config);
        onClose();
      }}
      onClose={onClose}
    >
      <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-900">
          {t("images.note.text")}
        </label>
        <textarea
          id={id}
          rows={6}
          maxLength={2000}
          value={text}
          onChange={(e) => setText(e.target.value)}
          aria-describedby={`${id}-help`}
          className={field}
        />
        <p id={`${id}-help`} className="mt-1 text-xs text-gray-700">
          {t("images.note.help")}
        </p>
      </div>
    </FormDialog>
  );
}

/**
 * Add or change a picture (`image` widget, dashboard-images IMG-08): the organisation's pictures to pick from, Upload a
 * picture (a file button or drag and drop, the limits said first) and the required description. A picture is deleted
 * here by whoever uploaded it or an Owner or Admin, after a confirmation.
 */
export function PictureDialog({
  open,
  orgId,
  initial,
  onDone,
  onClose,
}: {
  open: boolean;
  orgId: string;
  initial?: { asset: string; alt: string };
  onDone: (config: { asset: string; alt: string }) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { current } = useOrg();
  const me = useQuery(meQuery());
  const ids = { file: useId(), alt: useId(), list: useId() };
  const images = useQuery({ ...imagesQuery(orgId), enabled: open });
  const [asset, setAsset] = useState(initial?.asset ?? "");
  const [alt, setAlt] = useState(initial?.alt ?? "");
  const [problem, setProblem] = useState<string>();
  const [deleting, setDeleting] = useState<OrgImage>();

  const upload = useMutation({
    mutationFn: (file: File) => uploadImage(orgId, file),
    onSuccess: (img) => {
      setAsset(img.id);
      void qc.invalidateQueries({ queryKey: imagesKey(orgId) });
    },
    onError: (e) => setProblem(imageError(e, t)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteImage(orgId, id),
    onSuccess: (_, id) => {
      if (asset === id) setAsset("");
      void qc.invalidateQueries({ queryKey: imagesKey(orgId) });
    },
    onError: (e) => setProblem(imageError(e, t)),
  });

  function send(file: File | undefined) {
    if (!file) return;
    const p = fileProblem(file);
    setProblem(p ? t(p) : undefined);
    if (!p) upload.mutate(file);
  }
  function onDrop(e: DragEvent) {
    e.preventDefault();
    send(e.dataTransfer.files[0]);
  }

  const items = images.data?.items ?? [];
  const mayDelete = (i: OrgImage) => isAdminRole(current?.role) || (!!i.created_by && i.created_by === me.data?.id);
  const ready = !!asset && alt.trim().length > 0 && items.some((i) => i.id === asset);

  return (
    <>
      <FormDialog
        open={open}
        title={t(initial ? "images.picture.changeTitle" : "images.picture.title")}
        submitLabel={t(initial ? "images.picture.save" : "images.picture.add")}
        ready={ready}
        busy={upload.isPending}
        error={problem}
        onSubmit={() => {
          onDone({ asset, alt: alt.trim().slice(0, 100) });
          onClose();
        }}
        onClose={onClose}
      >
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          className="rounded-md border border-dashed border-gray-300 p-3 text-sm text-gray-800"
        >
          <label htmlFor={ids.file} className="block font-medium text-gray-900">
            {t("images.picture.upload")}
          </label>
          <input
            id={ids.file}
            type="file"
            accept="image/jpeg,image/png"
            disabled={upload.isPending || items.length >= IMAGE_LIMIT}
            aria-describedby={`${ids.file}-help`}
            onChange={(e) => {
              send(e.target.files?.[0]);
              e.target.value = "";
            }}
            className="mt-1 block w-full text-sm"
          />
          <p id={`${ids.file}-help`} className="mt-1 text-xs text-gray-700">
            {items.length >= IMAGE_LIMIT ? t("images.error.image_limit") : t("images.picture.limits")}
          </p>
          {upload.isPending && (
            <p role="status" className="mt-1 text-xs text-gray-800">
              {t("images.picture.uploading")}
            </p>
          )}
        </div>

        <fieldset>
          <legend id={ids.list} className="text-sm font-medium text-gray-900">
            {t("images.picture.choose")}
          </legend>
          {images.isPending ? (
            <p className="mt-1 text-sm text-gray-800">{t("images.loading")}</p>
          ) : items.length === 0 ? (
            <p className="mt-1 text-sm text-gray-800">{t("images.picture.none")}</p>
          ) : (
            <ul className="mt-2 grid max-h-64 grid-cols-3 gap-2 overflow-auto">
              {items.map((i) => (
                <li key={i.id} className="relative">
                  <label className="block cursor-pointer rounded-md border border-gray-200 p-1 has-checked:border-brand has-checked:ring-2 has-checked:ring-brand has-focus-visible:outline-2 has-focus-visible:outline-brand">
                    <input
                      type="radio"
                      name={ids.list}
                      value={i.id}
                      checked={asset === i.id}
                      onChange={() => setAsset(i.id)}
                      className="sr-only"
                    />
                    <Thumb orgId={orgId} image={i} />
                    <span className="block truncate text-xs text-gray-800">{i.name}</span>
                  </label>
                  {mayDelete(i) && (
                    <button
                      type="button"
                      aria-label={t("images.picture.delete", { name: i.name })}
                      onClick={() => setDeleting(i)}
                      className="absolute top-1 right-1 grid size-6 place-items-center rounded-full bg-white/90 text-gray-800 shadow hover:bg-red-50 hover:text-red-800"
                    >
                      ✕
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </fieldset>

        <div>
          <label htmlFor={ids.alt} className="block text-sm font-medium text-gray-900">
            {t("images.picture.alt")}
          </label>
          <input
            id={ids.alt}
            required
            maxLength={100}
            value={alt}
            onChange={(e) => setAlt(e.target.value)}
            aria-describedby={`${ids.alt}-help`}
            className={field}
          />
          <p id={`${ids.alt}-help`} className="mt-1 text-xs text-gray-700">
            {t("images.picture.altHelp")}
          </p>
        </div>
      </FormDialog>
      <ConfirmDialog
        open={!!deleting}
        title={t("images.picture.deleteTitle")}
        confirmLabel={t("images.picture.deleteGo")}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting.id);
          setDeleting(undefined);
        }}
        onClose={() => setDeleting(undefined)}
      >
        <p>{t("images.picture.deleteConfirm", { name: deleting?.name ?? "" })}</p>
      </ConfirmDialog>
    </>
  );
}

function Thumb({ orgId, image }: { orgId: string; image: OrgImage }) {
  const blob = useQuery(imageBlobQuery(orgId, image.id));
  const url = blob.data ? pictureUrl(blob.data) : undefined;
  return url ? (
    <img src={url} alt="" className="h-20 w-full rounded object-cover" />
  ) : (
    <div aria-hidden="true" className="h-20 w-full animate-pulse rounded bg-gray-100" />
  );
}
